import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';

import { LanguageSelector } from '@features/language-switch';
import { MarketSwitch } from '@features/market-switch';
import { Button } from '@shared/ui/button';
import { DASHBOARD_LOGIN_URL } from '@shared/config';

/**
 * The supplied brand lockup — the SAME asset the marketing `SiteNav` renders.
 *
 * The funnel used to draw its own mark (a Lucide `Clock` glyph on a coloured
 * square plus a text wordmark), so the moment a visitor crossed from the site
 * into checkout the brand changed (DIT-399; same class of defect as DIT-394,
 * which fixed the footer logo and missed this bar).
 *
 * Bundled, not on a CDN: DIT-392 moved the marketing nav onto the file from the
 * customer's logo pack, and "same asset as SiteNav" has to keep meaning the same
 * file — otherwise the two bars drift apart again the next time one of them moves.
 */
const BRAND_LOGO = '/done-it-logo.png';

/**
 * Funnel header — the marketing nav bar in "checkout" mode.
 *
 * Same shell, same tokens, same brand, same switchers as `SiteNav`, minus the
 * marketing nav links and the TRY FREE CTA: the flow stays distraction-free, but
 * it no longer reads as a different product. Below `md` the switchers move into a
 * sheet behind a Menu button — the bar previously had no mobile treatment at all
 * and simply crowded on small screens.
 *
 * It deliberately does NOT reuse `SiteNav` itself: that component is vendored 1:1
 * from `done-it-design`, renders the whole marketing menu system, and resolves its
 * links through the marketing `LocaleContext`, which the funnel does not provide.
 */
export default function Header() {
  const { t } = useTranslation('common');
  const { lang } = useParams<{ lang?: string }>();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const root = lang ? `/${lang}` : '/';

  // A step change closes the sheet — otherwise switching language from inside it
  // navigates and leaves the overlay covering the next screen.
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white font-ds">
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-6 px-4 md:px-10">
        <Link to={root} aria-label={t('nav.brandHomeAria')} className="flex items-center">
          {/* Fixed width/height so the bar doesn't reflow while the logo loads. */}
          <img
            src={BRAND_LOGO}
            alt="Done-it"
            width={114}
            height={40}
            className="h-9 w-auto md:h-10"
            decoding="async"
          />
        </Link>

        {/* Desktop cluster */}
        <div className="hidden items-center gap-5 md:flex">
          <MarketSwitch />
          <LanguageSelector />
          <Button to={DASHBOARD_LOGIN_URL} variant="outline" className="whitespace-nowrap">
            {t('nav.login')}
          </Button>
        </div>

        {/* Mobile trigger */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-label={t('nav.menu')}
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink-soft transition-colors hover:text-ink md:hidden"
        >
          {menuOpen ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-line bg-white px-4 py-4 md:hidden">
          <div className="flex flex-col gap-4">
            <MarketSwitch />
            <LanguageSelector />
            <Button to={DASHBOARD_LOGIN_URL} variant="outline" className="w-full">
              {t('nav.login')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
